import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputAdornment,
  Fade,
  Zoom,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  NetworkCheck as NetworkCheckIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Close,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Inventory as PackageIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  Code as CodeIcon,
  Build as BuildIcon,
  BugReport as BugReportIcon,
  AutoAwesome as MagicIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { PieChart, Pie, ResponsiveContainer, Tooltip as RechartsTooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import dashboardApiService from '../../services/dashboard/DashboardApiService';
import complianceApiService from '../../services/compliance/ComplianceApiService';
import alertsApiService from '../../services/alerts/AlertsApiService';
import vulnerabilityApiService from '../../services/vulnerabilities/VulnerabilityApiService';
import scaApiService from '../../services/sca/ScaApiService';
import mitreApiService from '../../services/mitre/MitreApiService';
import eventsApiService from '../../services/events/EventsApiService';
import packagesApiService from '../../services/packages/PackagesApiService';
import attackApiService from '../../services/alerts/AttackApiService';
import sshApiService from '../../services/ssh/SshApiService';

const AgentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [scaData, setScaData] = useState(null);
  const [mitreData, setMitreData] = useState(null);
  const [eventsData, setEventsData] = useState(null);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingSca, setLoadingSca] = useState(true);
  const [loadingMitre, setLoadingMitre] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [selectedComplianceStandard, setSelectedComplianceStandard] = useState('all');
  const [selectedScaFilter, setSelectedScaFilter] = useState('all'); // 'all', 'passed', 'failed'
  const [selectedScaPolicy, setSelectedScaPolicy] = useState(null);
  const [packagesData, setPackagesData] = useState(null);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [attackData, setAttackData] = useState(null);
  const [loadingAttacks, setLoadingAttacks] = useState(true);
  const [vulnerabilityData, setVulnerabilityData] = useState(null);
    const [loadingVulnerabilities, setLoadingVulnerabilities] = useState(true);
  const [sshAlerts, setSshAlerts] = useState([]);
  const [loadingSshAlerts, setLoadingSshAlerts] = useState(true);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([]); // { role: 'user' | 'assistant', content: string }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const aiInputRef = useRef(null);

  const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

  const renderMessageContent = (content) => {
    if (!content) return null;
    const blocks = content.split(/```/);
    return blocks.map((block, blockIndex) => {
      const isCodeBlock = blockIndex % 2 === 1;
      if (isCodeBlock) {
        return (
          <Box
            key={`code-${blockIndex}`}
            component="pre"
            sx={{
              mt: 1,
              mb: 1,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              backgroundColor: 'rgba(15,23,42,0.9)',
              overflowX: 'auto',
              fontFamily: 'SFMono-Regular,Menlo,Monaco,Consolas,\"Liberation Mono\",\"Courier New\",monospace',
              fontSize: 12.5,
              border: '1px solid rgba(55,65,81,0.8)'
            }}
          >
            <code>{block.trim()}</code>
          </Box>
        );
      }

      const lines = block.split(/(\n)/);
      return lines.map((line, lineIndex) => {
        if (line === '\n') {
          return <br key={`br-${blockIndex}-${lineIndex}`} />;
        }
        const parts = line.split(/(\s+)/);
        return (
          <React.Fragment key={`line-${blockIndex}-${lineIndex}`}>
            {parts.map((part, index) => {
              if (part.startsWith('http://') || part.startsWith('https://')) {
                return (
                  <Link
                    key={`link-${blockIndex}-${lineIndex}-${index}`}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: '#93c5fd' }}
                  >
                    {part}
                  </Link>
                );
              }

              if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
                const codeText = part.slice(1, -1);
                return (
                  <Box
                    key={`code-inline-${blockIndex}-${lineIndex}-${index}`}
                    component="span"
                    sx={{
                      fontFamily:
                        'SFMono-Regular,Menlo,Monaco,Consolas,\"Liberation Mono\",\"Courier New\",monospace',
                      fontSize: '0.85em',
                      backgroundColor: 'rgba(15,23,42,0.9)',
                      borderRadius: 0.75,
                      px: 0.5,
                      py: 0.1
                    }}
                  >
                    {codeText}
                  </Box>
                );
              }

              return part;
            })}
          </React.Fragment>
        );
      });
    });
  };

  const aiSuggestedQuestions = [
    'Give me a summary of this agent, including status and OS.',
    'How many CVEs does this agent have by severity?',
    'Summarize the SCA findings for this agent.',
    'Are there any recent compliance alerts for this agent?',
    'Show any notable SSH login alerts for this agent.'
  ];

  const fetchAgentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const agentData = await dashboardApiService.getAgentById(id);
      setAgent(agentData);
    } catch (err) {
      setError(`Failed to fetch agent details: ${err.message}`);
      console.error('Agent details fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchComplianceData = useCallback(async () => {
    try {
      const response = await complianceApiService.getComplianceAlerts(id);
      const processedData = complianceApiService.processComplianceData(response.alerts || []);
      setComplianceData(processedData);
    } catch (err) {
      console.error('Compliance data fetch error:', err);
    }
  }, [id]);

  const fetchSecurityAlerts = useCallback(async () => {
    try {
      setLoadingAlerts(true);
      const alertsResponse = await alertsApiService.getAlertsByAgent(id);
      setSecurityAlerts(alertsResponse.alerts || alertsResponse || []);
    } catch (err) {
      console.error('Security alerts fetch error:', err);
    } finally {
      setLoadingAlerts(false);
    }
  }, [id]);

  const fetchScaData = useCallback(async () => {
    try {
      setLoadingSca(true);
      const scaResponse = await scaApiService.getLatestReportsByAgent(id);
      setScaData(scaResponse);
    } catch (err) {
      console.error('SCA data fetch error:', err);
    } finally {
      setLoadingSca(false);
    }
  }, [id]);


  const fetchMitreData = useCallback(async () => {
    try {
      setLoadingMitre(true);
      const mitreResponse = await mitreApiService.getMitreAttackData(id, 30);
      setMitreData(mitreResponse);
    } catch (err) {
      console.error('MITRE data fetch error:', err);
    } finally {
      setLoadingMitre(false);
    }
  }, [id]);

  const fetchEventsData = useCallback(async () => {
    try {
      setLoadingEvents(true);
      const eventsResponse = await eventsApiService.getEventsEvolution(id, '30m', 30);
      setEventsData(eventsResponse);
    } catch (err) {
      console.error('Events data fetch error:', err);
    } finally {
      setLoadingEvents(false);
    }
  }, [id]);

  const fetchPackagesData = useCallback(async () => {
    try {
      setLoadingPackages(true);
      const packagesResponse = await packagesApiService.getAgentPackages(id);
      setPackagesData(packagesResponse);
    } catch (err) {
      console.error('Packages data fetch error:', err);
    } finally {
      setLoadingPackages(false);
    }
  }, [id]);

  const fetchAttackData = useCallback(async () => {
    try {
      setLoadingAttacks(true);
      const attackResponse = await attackApiService.getAttackAlerts(id);
      const processedData = attackApiService.processAttackData(attackResponse.alerts || attackResponse || []);
      setAttackData(processedData);
    } catch (err) {
      console.error('Attack data fetch error:', err);
    } finally {
      setLoadingAttacks(false);
    }
  }, [id]);

  const fetchVulnerabilityData = useCallback(async () => {
    try {
      setLoadingVulnerabilities(true);
      const vulnResponse = await vulnerabilityApiService.getVulnerabilitiesByAgent(id);
      const vulnerabilities = Array.isArray(vulnResponse) ? vulnResponse : [];
      const processedData = vulnerabilityApiService.processVulnerabilityData(vulnerabilities);
      setVulnerabilityData(processedData);
    } catch (err) {
      console.error('Vulnerability data fetch error:', err);
    } finally {
      setLoadingVulnerabilities(false);
    }
  }, [id]);

  const fetchSshAlerts = useCallback(async () => {
    try {
      setLoadingSshAlerts(true);
      const response = await sshApiService.getSshAlerts(id);
      setSshAlerts(response.alerts || []);
    } catch (err) {
      console.error('SSH alerts fetch error:', err);
    } finally {
      setLoadingSshAlerts(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchAgentDetails();
      fetchComplianceData();
      fetchSecurityAlerts();
      fetchScaData();
      fetchMitreData();
      fetchEventsData();
      fetchPackagesData();
      fetchAttackData();
      fetchVulnerabilityData();
      fetchSshAlerts();
      fetchSshAlerts();
    }
  }, [id, fetchAgentDetails, fetchComplianceData, fetchSecurityAlerts, fetchScaData, fetchMitreData, fetchEventsData, fetchPackagesData, fetchAttackData, fetchVulnerabilityData, fetchSshAlerts]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'disconnected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="status-icon-active" />;
      case 'disconnected':
        return <CancelIcon className="status-icon-disconnected" />;
      default:
        return <InfoIcon className="status-icon-default" />;
    }
  };

  const formatLastKeepAlive = (lastKeepAlive) => {
    if (lastKeepAlive === '9999-12-31T23:59:59+00:00') {
      return 'Never disconnected';
    }
    return new Date(lastKeepAlive).toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };



  const getPackageStats = () => {
    if (!packagesData?.packages) return { total: 0, deb: 0, rpm: 0, large: 0 };
    
    const packages = packagesData.packages;
    return {
      total: packages.length,
      deb: packages.filter(p => p.format === 'deb').length,
      rpm: packages.filter(p => p.format === 'rpm').length,
      large: packages.filter(p => p.size > 1000000).length
    };
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getSeverityFromLevel = (level) => {
    if (level >= 10) return 'Critical';
    if (level >= 7) return 'High';
    if (level >= 4) return 'Medium';
    if (level >= 1) return 'Low';
    return 'Info';
  };

  const handleOpenAiDialog = () => {
    // Push latest agent context to backend cache for faster AI responses
    try {
      const contextJson = buildAgentContext();
      const token = localStorage.getItem('cognito_token');
      fetch(`${API_BASE_URL}/ai/agent-context`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          agent_id: id,
          context_json: contextJson
        })
      }).catch(() => {
        // Non-blocking best-effort cache; ignore failures
      });
    } catch (e) {
      // Ignore context build/push errors for UI
      console.error('Failed to push agent context for AI cache:', e);
    }

    setAiDialogOpen(true);
    setAiError(null);
  };

  const handleCloseAiDialog = () => {
    setAiDialogOpen(false);
    setAiMessages([]);
    setAiError(null);
  };

  const buildAgentContext = () => {
    if (!agent) {
      return 'No agent context available.';
    }
    try {
      const summary = {
        id: agent.id,
        name: agent.name,
        status: agent.status,
        ip: agent.ip,
        os: agent.os,
        version: agent.version,
        manager: agent.manager,
        groups: agent.group || agent.groups,
        lastKeepAlive: agent.lastKeepAlive,
        disconnection_time: agent.disconnection_time,
        registerIP: agent.registerIP,
        node_name: agent.node_name,
        // High‑level page sections so AI can answer "what is SCA report", "any vulnerabilities", etc.
        sca: scaData ? {
          summary: scaData.sca_summary || null
        } : null,
        compliance: complianceData ? {
          overview: complianceData.overview || null,
          recentAlertsSample: complianceData.recentAlerts ? complianceData.recentAlerts.slice(0, 20) : []
        } : null,
        mitre: mitreData ? {
          totalEvents: Array.isArray(mitreData.events) ? mitreData.events.length : 0
        } : null,
        eventsEvolution: eventsData ? {
          totalPoints: Array.isArray(eventsData.events) ? eventsData.events.length : 0
        } : null,
        packages: packagesData ? {
          total: Array.isArray(packagesData.packages) ? packagesData.packages.length : 0
        } : null,
        attacks: attackData ? {
          totalAlerts: Array.isArray(attackData.alerts) ? attackData.alerts.length : undefined,
          summary: attackData.summary || null
        } : null,
        vulnerabilities: vulnerabilityData ? {
          totalVulnerabilities: vulnerabilityData.totalVulnerabilities || 0,
          severityStats: vulnerabilityData.severityStats || null,
          recentVulnerabilitiesSample: Array.isArray(vulnerabilityData.recentVulnerabilities)
            ? vulnerabilityData.recentVulnerabilities.slice(0, 20)
            : [],
          topPackages: Array.isArray(vulnerabilityData.topPackages)
            ? vulnerabilityData.topPackages
            : [],
          criticalVulnerabilitiesSample: Array.isArray(vulnerabilityData.criticalVulnerabilities)
            ? vulnerabilityData.criticalVulnerabilities.slice(0, 20)
            : []
        } : null,
        sshAlertsSample: Array.isArray(sshAlerts) ? sshAlerts.slice(0, 20) : []
      };
      return JSON.stringify(summary);
    } catch (e) {
      return 'No agent context available.';
    }
  };

  const handleSendAiPrompt = async () => {
    const prompt = aiInputRef.current ? aiInputRef.current.value : '';
    if (!prompt.trim()) {
      return;
    }

    setAiLoading(true);
    setAiError(null);
    setAiMessages((prev) => [...prev, { role: 'user', content: prompt }]);
    if (aiInputRef.current) {
      aiInputRef.current.value = '';
    }

    const context = buildAgentContext();

    try {
      const token = localStorage.getItem('cognito_token');
      const response = await fetch(`${API_BASE_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          agent_id: id,
          messages: [
            {
              role: 'system',
              content: `You are an assistant helping with questions about a single agent. Agent data (JSON): ${context}`
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 200
        })
      });

      if (!response.ok) {
        let extra = '';
        try {
          const errText = await response.text();
          extra = errText ? ` - ${errText}` : '';
        } catch (e) {
          // ignore parsing error
        }
        throw new Error(`Request failed with status ${response.status}${extra}`);
      }

      const data = await response.json();
      const content = data?.content || 'No response received.';
      setAiMessages((prev) => [...prev, { role: 'assistant', content }]);
    } catch (err) {
      console.error('AI assistant error (agent details):', err);
      setAiError(err.message || 'Failed to get response from AI assistant');
    } finally {
      setAiLoading(false);
    }
  };


  // Helper function to process SCA data
  const getScaReports = () => {
    if (!scaData || !scaData.sca_summary) {
      return [];
    }
    
    const summary = scaData.sca_summary;
    
    // Handle the new API format where sca_summary is a single object
    if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
      return [summary]; // Return as array with single policy
    }
    
    // Handle array format (legacy)
    if (Array.isArray(summary)) {
      return summary;
    }
    
    return [];
  };

  // Helper function to get filtered SCA checks
  const getFilteredScaChecks = (policy) => {
    if (!policy || !policy.checks) {
      return [];
    }

    if (selectedScaFilter === 'all') {
      return policy.checks;
    }

    return policy.checks.filter(check => check.result === selectedScaFilter);
  };

  // Helper function to get SCA summary stats
  const getScaSummaryStats = () => {
    if (!scaData || !scaData.sca_summary) {
      return { totalPolicies: 0, totalPassed: 0, totalFailed: 0, totalNotApplicable: 0, avgScore: 0 };
    }

    const summary = scaData.sca_summary;
    
    // Handle the new API format where sca_summary is a single object
    if (summary && typeof summary === 'object' && !Array.isArray(summary)) {
      return {
        totalPolicies: 1,
        totalPassed: summary.passed || 0,
        totalFailed: summary.failed || 0,
        totalNotApplicable: summary.not_applicable || 0,
        avgScore: summary.score || 0
      };
    }

    // Handle array format (legacy)
    if (Array.isArray(summary)) {
      if (summary.length === 0) {
        return { totalPolicies: 0, totalPassed: 0, totalFailed: 0, totalNotApplicable: 0, avgScore: 0 };
      }

      const totalPolicies = summary.length;
      const totalPassed = summary.reduce((sum, report) => sum + (report.passed || 0), 0);
      const totalFailed = summary.reduce((sum, report) => sum + (report.failed || 0), 0);
      const totalNotApplicable = summary.reduce((sum, report) => sum + (report.not_applicable || 0), 0);
      const avgScore = totalPolicies > 0 ? Math.round(summary.reduce((sum, report) => sum + (report.score || 0), 0) / totalPolicies) : 0;

      return { totalPolicies, totalPassed, totalFailed, totalNotApplicable, avgScore };
    }

    return { totalPolicies: 0, totalPassed: 0, totalFailed: 0, totalNotApplicable: 0, avgScore: 0 };
  };

  // Helper function to filter compliance alerts by selected standard
  const getFilteredComplianceAlerts = () => {
    if (!complianceData || !complianceData.recentAlerts) {
      return [];
    }

    if (selectedComplianceStandard === 'all') {
      return complianceData.recentAlerts;
    }

    // Get the standard name from the selected key
    const getStandardName = (key) => {
      const names = {
        'pci_dss': 'PCI DSS',
        'hipaa': 'HIPAA',
        'gdpr': 'GDPR',
        'nist_800_53': 'NIST 800-53',
        'tsc': 'TSC',
        'gpg13': 'GPG13'
      };
      return names[key] || key.toUpperCase();
    };

    const selectedStandardName = getStandardName(selectedComplianceStandard);

    // Filter alerts by selected compliance standard name
    return complianceData.recentAlerts.filter(alert => 
      alert.standards && alert.standards.some(standard => 
        standard.name === selectedStandardName
      )
    );
  };


  // Helper function to process MITRE data
  const getMitreTopTactics = () => {
    if (!mitreData || !mitreData.events) {
      return [];
    }
    
    // Count tactics from all events
    const tacticCounts = {};
    
    mitreData.events.forEach(event => {
      if (event.mitre && event.mitre.tactic) {
        event.mitre.tactic.forEach(tactic => {
          tacticCounts[tactic] = (tacticCounts[tactic] || 0) + 1;
        });
      }
    });
    
    // Convert to array and sort by count
    const sortedTactics = Object.entries(tacticCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5); // Top 5 tactics
    
    return sortedTactics;
  };

  // Helper function to process Events evolution data
  const getEventsChartData = () => {
    if (!eventsData || !eventsData.events) {
      return [];
    }
    
    // Process events data for chart
    return eventsData.events.map(item => ({
      time: new Date(item.timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      events: item.count || item.events || 0
    }));
  };


  const refreshAllData = useCallback(() => {
    fetchAgentDetails();
    fetchComplianceData();
    fetchSecurityAlerts();
    fetchScaData();
    fetchMitreData();
    fetchEventsData();
    fetchAttackData();
  }, [fetchAgentDetails, fetchComplianceData, fetchSecurityAlerts, fetchScaData, fetchMitreData, fetchEventsData, fetchAttackData]);

  if (loading) {
    return (
      <Box className="agent-details-loading">
        <CircularProgress size={40} />
        <Typography variant="body1" className="loading-text">Loading agent details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="agent-details-error">
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchAgentDetails}
          startIcon={<RefreshIcon />}
          className="retry-button"
        >
          Retry
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/agents')}
          startIcon={<ArrowBackIcon />}
          className="back-button"
        >
          Back to Agents
        </Button>
      </Box>
    );
  }

  if (!agent) {
    return (
      <Box className="agent-not-found">
        <Typography variant="h6">Agent not found</Typography>
        <Typography variant="body2">The requested agent could not be found.</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/agents')}
          startIcon={<ArrowBackIcon />}
          className="back-button"
        >
          Back to Agents
        </Button>
      </Box>
    );
  }

  return (
    <Box className="agent-details" sx={{ position: 'relative', minHeight: '100%' }}>
      {/* Header with Breadcrumbs */}
      <Box className="agent-details-header">
        <Breadcrumbs className="breadcrumbs">
          <Link 
            color="inherit" 
            onClick={() => navigate('/agents')}
            className="breadcrumb-link"
          >
            Agents
          </Link>
          <Typography color="text.primary">{agent.name}</Typography>
        </Breadcrumbs>
        
        <Box className="header-actions">
          <IconButton 
            onClick={() => navigate('/agents')}
            className="back-icon-button"
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton 
            onClick={refreshAllData}
            className="refresh-icon-button"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Agent Overview Card */}
      <Card className="agent-overview-card">
        <CardContent className="agent-overview-content">
          <Box className="agent-overview-header">
            <Box className="agent-info">
              <Avatar className="agent-details-avatar">
                <ComputerIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" className="agent-details-name">
                  {agent.name}
                </Typography>
                <Typography variant="body1" className="agent-details-id">
                  Agent ID: {agent.id}
                </Typography>
              </Box>
            </Box>
            <Box className="agent-status-container">
              {getStatusIcon(agent.status)}
              <Chip 
                label={agent.status.toUpperCase()}
                color={getStatusColor(agent.status)}
                size="large"
                className="agent-details-status-chip"
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Details Grid */}
      <Grid container spacing={3} className="agent-details-grid" sx={{ mt: 0 }}>
        {/* System Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="details-card">
            <CardContent>
              <Box className="card-header">
                <ComputerIcon className="card-icon" />
                <Typography variant="h6" className="card-title">
                  System Information
                </Typography>
              </Box>
              <Divider className="card-divider" />
              <Box className="details-list">
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Operating System</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.os.name} {agent.os.version}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Platform</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.os.platform} ({agent.os.arch})
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Codename</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.os.codename || 'N/A'}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">System Info</Typography>
                  <Typography variant="body2" className="detail-value system-info">
                    {agent.os.uname}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Network Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="details-card">
            <CardContent>
              <Box className="card-header">
                <NetworkCheckIcon className="card-icon" />
                <Typography variant="h6" className="card-title">
                  Network Information
                </Typography>
              </Box>
              <Divider className="card-divider" />
              <Box className="details-list">
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">IP Address</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.ip}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Register IP</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.registerIP}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Manager</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.manager}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Node Name</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.node_name}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* OpenShield Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="details-card">
            <CardContent>
              <Box className="card-header">
                <SecurityIcon className="card-icon" />
                <Typography variant="h6" className="card-title">
                  OpenShield Information
                </Typography>
              </Box>
              <Divider className="card-divider" />
              <Box className="details-list">
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Version</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.version}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Status Code</Typography>
                  <Typography variant="body1" className="detail-value">
                    {agent.status_code}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Config Sum</Typography>
                  <Typography variant="body2" className="detail-value config-sum">
                    {agent.configSum || 'N/A'}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Merged Sum</Typography>
                  <Typography variant="body2" className="detail-value config-sum">
                    {agent.mergedSum || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity & Status */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card className="details-card">
            <CardContent>
              <Box className="card-header">
                <ScheduleIcon className="card-icon" />
                <Typography variant="h6" className="card-title">
                  Activity & Status
                </Typography>
              </Box>
              <Divider className="card-divider" />
              <Box className="details-list">
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Date Added</Typography>
                  <Typography variant="body1" className="detail-value">
                    {formatDate(agent.dateAdd)}
                  </Typography>
                </Box>
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Last Keep Alive</Typography>
                  <Typography variant="body1" className="detail-value">
                    {formatLastKeepAlive(agent.lastKeepAlive)}
                  </Typography>
                </Box>
                {agent.disconnection_time && (
                  <Box className="detail-item">
                    <Typography variant="body2" className="detail-label">Disconnection Time</Typography>
                    <Typography variant="body1" className="detail-value disconnection-time">
                      {formatDate(agent.disconnection_time)}
                    </Typography>
                  </Box>
                )}
                <Box className="detail-item">
                  <Typography variant="body2" className="detail-label">Config Status</Typography>
                  <Chip 
                    label={agent.group_config_status}
                    color={agent.group_config_status === 'synced' ? 'success' : 'warning'}
                    size="small"
                    className="config-status-chip"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>


        {/* SSH Login Attempts */}
        <Grid item xs={12}>
          <Card className="details-card">
            <CardContent>
              <Box className="card-header">
                <SecurityIcon className="card-icon" />
                <Typography variant="h6" className="card-title">
                  SSH Login Attempts
                </Typography>
              </Box>
              <Divider className="card-divider" />
              {loadingSshAlerts ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Log Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sshAlerts.length > 0 ? (
                        sshAlerts.slice(0, 10).map((alert, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{formatTimestamp(alert['@timestamp'])}</TableCell>
                            <TableCell>{alert.rule.description}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{alert.full_log}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} align="center">No SSH alerts found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Groups */}

        {/* Dashboard Overview Grid - Exact Replica */}
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {/* Row 1: Events Evolution and MITRE ATT&CK */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: 280, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body1" fontWeight="500" sx={{ fontSize: '14px' }}>
                      Events count evolution
                    </Typography>
                    {loadingEvents && <CircularProgress size={16} />}
                  </Box>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={loadingEvents ? [] : getEventsChartData()}>
                      <CartesianGrid strokeDasharray="2 2" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="time" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#666' }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#666' }}
                      />
                      <RechartsTooltip />
                      <Line 
                        type="monotone" 
                        dataKey="events" 
                        stroke="#4285f4" 
                        strokeWidth={2}
                        dot={{ fill: '#4285f4', strokeWidth: 2, r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <Typography variant="caption" sx={{ color: '#666', fontSize: '10px' }}>
                    timestamp per 30 minutes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ 
                height: 280, 
                border: '1px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(234, 67, 53, 0.15)',
                  transform: 'translateY(-2px)',
                  borderColor: '#ea4335'
                }
              }}
              onClick={() => navigate(`/mitre-attack?agent=${id}`)}>
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #ea4335 0%, #d33b2c 100%)',
                  color: 'white',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      p: 0.8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <SecurityIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '14px' }}>
                        MITRE ATT&CK
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '11px' }}>
                        Click to view analysis
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingMitre && <CircularProgress size={16} sx={{ color: 'white' }} />}
                    <Chip 
                      label={getMitreTopTactics().length}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '11px',
                        height: 20
                      }}
                    />
                  </Box>
                </Box>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: '12px', fontWeight: '500' }}>
                    Top Tactics
                  </Typography>
                  <Box>
                    {loadingMitre ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : getMitreTopTactics().length > 0 ? (
                      getMitreTopTactics().map((tactic, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          py: 1, 
                          px: 1,
                          borderBottom: index < 4 ? '1px solid #f5f5f5' : 'none',
                          borderRadius: 1,
                          '&:hover': { backgroundColor: '#f8f9fa' }
                        }}>
                          <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: '500' }}>
                            {tactic.name || tactic.tactic}
                          </Typography>
                          <Chip 
                            label={tactic.count || tactic.events || 0}
                            size="small"
                            sx={{ 
                              fontSize: '10px', 
                              height: 18,
                              backgroundColor: '#ea4335',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <SecurityIcon sx={{ fontSize: 36, color: '#ccc', mb: 1 }} />
                        <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
                          No tactics data available
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Row 2: Compliance and Compliance Alerts */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: 280, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body1" fontWeight="500" sx={{ mb: 1, fontSize: '14px' }}>
                    Compliance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={selectedComplianceStandard}
                        onChange={(e) => setSelectedComplianceStandard(e.target.value)}
                        displayEmpty
                        sx={{ 
                          fontSize: '12px', 
                          height: 28,
                          backgroundColor: selectedComplianceStandard !== 'all' ? '#e3f2fd' : 'white',
                          border: selectedComplianceStandard !== 'all' ? '1px solid #2196f3' : '1px solid #e0e0e0'
                        }}
                      >
                        <MenuItem value="all" sx={{ fontSize: '12px' }}>
                          📊 All Standards
                        </MenuItem>
                        {complianceData && Object.entries(complianceData.complianceStandards).map(([key, standard]) => (
                          <MenuItem key={key} value={key} sx={{ fontSize: '12px' }}>
                            🔒 {standard.name} ({standard.count})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {selectedComplianceStandard !== 'all' && (
                      <Chip 
                        label="Filtered" 
                        size="small" 
                        color="primary" 
                        sx={{ fontSize: '10px', height: 20 }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', height: 160 }}>
                    <ResponsiveContainer width="45%" height={160}>
                      <PieChart>
                        <Pie
                          data={complianceData ? Object.entries(complianceData.complianceStandards).map(([key, standard], index) => {
                            // Dynamic color palette for all standards
                            const colors = ['#34a853', '#4285f4', '#ea4335', '#fbbc04', '#9c27b0', '#ff6d01', '#00bcd4', '#795548'];
                            return {
                              name: standard.name,
                              value: standard.count,
                              fill: colors[index % colors.length]
                            };
                          }) : []}
                          cx="50%"
                          cy="50%"
                          innerRadius={18}
                          outerRadius={50}
                          paddingAngle={1}
                          dataKey="value"
                        />
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ 
                      ml: 1, 
                      flex: 1, 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      gap: 0.3
                    }}>
                      {complianceData ? Object.entries(complianceData.complianceStandards).map(([key, standard], index) => {
                        // Same dynamic color palette
                        const colors = ['#34a853', '#4285f4', '#ea4335', '#fbbc04', '#9c27b0', '#ff6d01', '#00bcd4', '#795548'];
                        return (
                          <Box key={key} sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            backgroundColor: selectedComplianceStandard === key ? '#f0f0f0' : 'transparent',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                               onClick={() => setSelectedComplianceStandard(key)}>
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              backgroundColor: colors[index % colors.length],
                              borderRadius: '50%', 
                              mr: 1,
                              flexShrink: 0
                            }} />
                            <Typography variant="caption" sx={{ 
                              fontSize: '9px',
                              fontWeight: selectedComplianceStandard === key ? 'bold' : 'normal',
                              color: selectedComplianceStandard === key ? '#333' : '#666',
                              lineHeight: 1.2
                            }}>
                              {standard.name} ({standard.count})
                            </Typography>
                          </Box>
                        );
                      }) : (
                        <Typography variant="caption" sx={{ fontSize: '10px' }}>Loading...</Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Compliance Alerts Summary - Right of Pie Chart */}
            <Grid size={{ xs: 12, md: 6 }}>
              {complianceData && complianceData.recentAlerts.length > 0 ? (
                <Card sx={{ 
                  height: 280,
                  border: '1px solid #e0e0e0', 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(66, 133, 244, 0.15)',
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => navigate(`/compliance?agent=${id}&standard=${selectedComplianceStandard}`)}>
                  <Box sx={{ 
                    background: 'linear-gradient(135deg, #4285f4 0%, #1976d2 100%)',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AssessmentIcon sx={{ fontSize: 20 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '14px' }}>
                          Compliance Alerts
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '11px' }}>
                          Click to view details
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={getFilteredComplianceAlerts().length}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '12px'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 2, height: 'calc(100% - 64px)', overflow: 'hidden' }}>
                    <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#4285f4', fontWeight: 'bold', fontSize: '24px' }}>
                          {getFilteredComplianceAlerts().length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '10px' }}>
                          {selectedComplianceStandard === 'all' ? 'Total Alerts' : 'Filtered Alerts'}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#34a853', fontWeight: 'bold', fontSize: '24px' }}>
                          {Object.keys(complianceData.complianceStandards).length}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '10px' }}>
                          Standards
                        </Typography>
                      </Box>
                      
                      {selectedComplianceStandard !== 'all' && complianceData.complianceStandards[selectedComplianceStandard] && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#ea4335', fontWeight: 'bold', fontSize: '24px' }}>
                            {complianceData.complianceStandards[selectedComplianceStandard].violationCount}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '10px' }}>
                            Violations
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontSize: '12px' }}>
                      Recent Activity
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 140, overflowY: 'auto' }}>
                      {getFilteredComplianceAlerts().slice(0, 4).map((alert, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          p: 1,
                          backgroundColor: '#f8f9fa',
                          borderRadius: 1,
                          border: '1px solid #e9ecef'
                        }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ 
                              fontSize: '10px', 
                              fontWeight: '500', 
                              mb: 0.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {alert.description}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '9px', color: '#666' }}>
                              {formatTimestamp(alert.timestamp)}
                            </Typography>
                          </Box>
                          <Chip
                            label={getSeverityFromLevel(alert.level)}
                            color={getSeverityColor(getSeverityFromLevel(alert.level))}
                            size="small"
                            sx={{ fontSize: '8px', height: '16px', ml: 1 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card sx={{ height: 280, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center', color: '#666' }}>
                    <AssessmentIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>
                      No compliance alerts available
                    </Typography>
                  </Box>
                </Card>
              )}
            </Grid>

            {/* Row 3: Attack Alerts Section */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ 
                height: 280,
                border: '1px solid #e0e0e0', 
                borderRadius: 2, 
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(234, 67, 53, 0.15)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #ff5722 0%, #d84315 100%)',
                  color: 'white',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      p: 0.8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <WarningIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '14px' }}>
                        Attack Alerts
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '11px' }}>
                        Security threats detected
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingAttacks && <CircularProgress size={16} sx={{ color: 'white' }} />}
                    <Chip 
                      label={attackData?.totalAlerts || 0}
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '11px',
                        height: 20
                      }}
                    />
                  </Box>
                </Box>
                <CardContent sx={{ p: 2, height: 'calc(100% - 64px)', overflow: 'hidden' }}>
                  {loadingAttacks ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : attackData && attackData.totalAlerts > 0 ? (
                    <>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                          onClick={() => navigate(`/agents/${id}/attacks`)}
                        >
                          <Typography variant="h4" sx={{ color: '#ff5722', fontWeight: 'bold', fontSize: '24px' }}>
                            {attackData.totalAlerts}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '10px' }}>
                            Total Attacks
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                          onClick={() => navigate(`/agents/${id}/attacks?severity=critical`)}
                        >
                          <Typography variant="h4" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '24px' }}>
                            {attackData.severityStats.critical + attackData.severityStats.high}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '10px' }}>
                            Critical & High
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                          onClick={() => navigate(`/agents/${id}/attacks?severity=medium`)}
                        >
                          <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold', fontSize: '24px' }}>
                            {attackData.severityStats.medium}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '10px' }}>
                            Medium
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontSize: '12px' }}>
                        Recent Attack Alerts
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 120, overflowY: 'auto' }}>
                        {attackData.recentAlerts.slice(0, 3).map((alert, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            p: 1,
                            backgroundColor: '#fff3e0',
                            borderRadius: 1,
                            border: '1px solid #ffcc02'
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '10px', 
                                fontWeight: '500', 
                                mb: 0.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {alert.rule?.description || 'Attack detected'}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '9px', color: '#666' }}>
                                {alert.data?.srcip || alert.srcip || 'Unknown source'} • {formatTimestamp(alert.timestamp)}
                              </Typography>
                            </Box>
                            <Chip
                              label={attackApiService.categorizeAttackSeverity(alert.rule?.level || 0)}
                              color={attackApiService.getSeverityColor(attackApiService.categorizeAttackSeverity(alert.rule?.level || 0))}
                              size="small"
                              sx={{ fontSize: '8px', height: '16px', ml: 1 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <WarningIcon sx={{ fontSize: 36, color: '#ccc', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
                        No attack alerts detected
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Attack Sources and Types */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card sx={{ height: 280, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body1" fontWeight="500" sx={{ mb: 2, fontSize: '14px' }}>
                    Attack Analysis
                  </Typography>
                  {loadingAttacks ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : attackData && attackData.totalAlerts > 0 ? (
                    <Box sx={{ height: 220 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontSize: '12px' }}>
                        Top Attack Types
                      </Typography>
                      <Box sx={{ mb: 3, maxHeight: 90, overflowY: 'auto' }}>
                        {attackData.topAttackTypes.slice(0, 3).map((attack, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            py: 0.5, 
                            px: 1,
                            borderBottom: index < 2 ? '1px solid #f5f5f5' : 'none',
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f8f9fa' }
                          }}>
                            <Typography variant="body2" sx={{ 
                              fontSize: '11px', 
                              fontWeight: '500',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '70%'
                            }}>
                              {attack.type}
                            </Typography>
                            <Chip 
                              label={attack.count}
                              size="small"
                              sx={{ 
                                fontSize: '9px', 
                                height: 16,
                                backgroundColor: '#ff5722',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                      
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#333', fontSize: '12px' }}>
                        Top Source IPs
                      </Typography>
                      <Box sx={{ maxHeight: 90, overflowY: 'auto' }}>
                        {attackData.sourceIPs.slice(0, 3).map((source, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            py: 0.5, 
                            px: 1,
                            borderBottom: index < 2 ? '1px solid #f5f5f5' : 'none',
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f8f9fa' }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon sx={{ fontSize: 12, color: '#666' }} />
                              <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: '500' }}>
                                {source.ip}
                              </Typography>
                            </Box>
                            <Chip 
                              label={source.count}
                              size="small"
                              sx={{ 
                                fontSize: '9px', 
                                height: 16,
                                backgroundColor: '#f44336',
                                color: 'white',
                                fontWeight: 'bold'
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <SecurityIcon sx={{ fontSize: 36, color: '#ccc', mb: 1 }} />
                      <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
                        No attack data available
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Critical Vulnerabilities - Full Width */}
            <Grid size={{ xs: 12 }}>
              <Card sx={{ border: '1px solid #e0e0e0' }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
                  color: 'white',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugReportIcon />
                    <Typography variant="body1" fontWeight="500" sx={{ fontSize: '14px' }}>
                      Critical CVEs
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate(`/agents/${id}/vulnerabilities`)}
                    startIcon={<VisibilityIcon />}
                    sx={{
                      color: 'white',
                      borderColor: 'white',
                      fontSize: '10px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: 'white'
                      }
                    }}
                  >
                    View All CVEs
                  </Button>
                </Box>
                <CardContent sx={{ p: 2 }}>
                  {loadingVulnerabilities ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : vulnerabilityData && vulnerabilityData.totalVulnerabilities > 0 ? (
                    <>
                      {/* Statistics Row */}
                      <Box sx={{ display: 'flex', gap: 3, mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#e9ecef' }
                          }}
                          onClick={() => navigate(`/agents/${id}/vulnerabilities`)}
                        >
                          <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold', fontSize: '28px' }}>
                            {vulnerabilityData.totalVulnerabilities}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                            Total CVEs
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#e9ecef' }
                          }}
                          onClick={() => navigate(`/agents/${id}/vulnerabilities?severity=critical`)}
                        >
                          <Typography variant="h4" sx={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '28px' }}>
                            {vulnerabilityData.severityStats.critical}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                            Critical
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#e9ecef' }
                          }}
                          onClick={() => navigate(`/agents/${id}/vulnerabilities?severity=high`)}
                        >
                          <Typography variant="h4" sx={{ color: '#ff5722', fontWeight: 'bold', fontSize: '28px' }}>
                            {vulnerabilityData.severityStats.high}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                            High
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#e9ecef' }
                          }}
                          onClick={() => navigate(`/agents/${id}/vulnerabilities?severity=medium`)}
                        >
                          <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold', fontSize: '28px' }}>
                            {vulnerabilityData.severityStats.medium}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                            Medium
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#e9ecef' }
                          }}
                          onClick={() => navigate(`/agents/${id}/vulnerabilities?severity=low`)}
                        >
                          <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold', fontSize: '28px' }}>
                            {vulnerabilityData.severityStats.low}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                            Low
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Typography variant="subtitle1" sx={{ mb: 2, color: '#333', fontSize: '16px', fontWeight: '600' }}>
                        Latest Top 20 Critical & High CVEs
                      </Typography>
                      
                      {/* Vulnerabilities Grid */}
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
                        gap: 2, 
                        maxHeight: 600, 
                        overflowY: 'auto',
                        pr: 1
                      }}>
                        {vulnerabilityData.criticalVulnerabilities.slice(0, 20).map((vuln, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            p: 2,
                            backgroundColor: vuln.vulnerability?.severity === 'critical' ? '#ffebee' : '#fff3e0',
                            borderRadius: 2,
                            border: `2px solid ${vuln.vulnerability?.severity === 'critical' ? '#f44336' : '#ff9800'}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }
                          }}
                          onClick={() => navigate(`/agents/${id}/vulnerabilities`)}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                              <Typography variant="body1" sx={{ 
                                fontSize: '14px', 
                                fontWeight: '600', 
                                color: '#333',
                                flex: 1
                              }}>
                                {vuln.vulnerability?.id || 'CVE-Unknown'}
                              </Typography>
                              <Chip
                                label={vuln.vulnerability?.severity || 'Unknown'}
                                color={vulnerabilityApiService.getSeverityColor(vuln.vulnerability?.severity)}
                                size="small"
                                sx={{ fontSize: '10px', fontWeight: '600' }}
                              />
                            </Box>
                            
                            <Typography variant="body2" sx={{ 
                              fontSize: '12px', 
                              color: '#666', 
                              mb: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {vuln.vulnerability?.title || vuln.vulnerability?.description || 'No description available'}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                              <Typography variant="caption" sx={{ fontSize: '11px', color: '#888', fontWeight: '500' }}>
                                📦 {vuln.package?.name || 'Unknown package'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" sx={{ 
                                  fontSize: '11px', 
                                  color: vuln.vulnerability?.score?.base >= 9 ? '#d32f2f' : vuln.vulnerability?.score?.base >= 7 ? '#ff5722' : '#ff9800',
                                  fontWeight: '600'
                                }}>
                                  CVSS: {vuln.vulnerability?.score?.base?.toFixed(1) || 'N/A'}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                      
                      {vulnerabilityData.criticalVulnerabilities.length > 20 && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={() => navigate(`/agents/${id}/vulnerabilities`)}
                            sx={{ fontSize: '12px' }}
                          >
                            View All {vulnerabilityData.criticalVulnerabilities.length} Critical & High CVEs
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <BugReportIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                      <Typography variant="body1" sx={{ fontSize: '16px', color: '#666' }}>
                        No vulnerabilities detected
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Row 4: SCA Security Configuration Assessment - Full width */}
            <Grid size={{ xs: 12 }}>
              <Card sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight="500" sx={{ fontSize: '14px' }}>
                        SCA: Security Configuration Assessment
                      </Typography>
                      {loadingSca && <CircularProgress size={16} />}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant={selectedScaFilter === 'all' ? 'contained' : 'outlined'}
                        onClick={() => setSelectedScaFilter('all')}
                        sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}
                      >
                        All
                      </Button>
                      <Button
                        size="small"
                        variant={selectedScaFilter === 'passed' ? 'contained' : 'outlined'}
                        color="success"
                        onClick={() => setSelectedScaFilter('passed')}
                        sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}
                      >
                        Passed
                      </Button>
                      <Button
                        size="small"
                        variant={selectedScaFilter === 'failed' ? 'contained' : 'outlined'}
                        color="error"
                        onClick={() => setSelectedScaFilter('failed')}
                        sx={{ fontSize: '10px', minWidth: 'auto', px: 1 }}
                      >
                        Failed
                      </Button>
                    </Box>
                  </Box>

                  {/* SCA Summary Stats */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                    {(() => {
                      const stats = getScaSummaryStats();
                      return (
                        <Box sx={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => setSelectedScaFilter('all')}>
                          <Typography variant="h4" sx={{ color: '#4285f4', fontWeight: 'bold', fontSize: '20px' }}>
                            {stats.totalPolicies}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '10px', color: '#666' }}>
                            Policies
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '12px', fontWeight: '500', py: 1 }}>Policy</TableCell>
                          <TableCell sx={{ fontSize: '12px', fontWeight: '500', py: 1 }}>End scan</TableCell>
                          <TableCell sx={{ fontSize: '12px', fontWeight: '500', py: 1 }}>Passed</TableCell>
                          <TableCell sx={{ fontSize: '12px', fontWeight: '500', py: 1 }}>Failed</TableCell>
                          <TableCell sx={{ fontSize: '12px', fontWeight: '500', py: 1 }}>Not appl...</TableCell>
                          <TableCell sx={{ fontSize: '12px', fontWeight: '500', py: 1 }}>Score</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingSca ? (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                              <CircularProgress size={24} />
                              <Typography variant="body2" sx={{ mt: 1, fontSize: '12px' }}>Loading SCA reports...</Typography>
                            </TableCell>
                          </TableRow>
                        ) : getScaReports().length > 0 ? (
                          getScaReports().map((report, index) => {
                            const passed = report.passed || 0;
                            const failed = report.failed || 0;
                            const notApplicable = report.not_applicable || 0;
                            const score = report.score || 0;
                            
                            return (
                              <TableRow 
                                key={index} 
                                hover 
                                sx={{ 
                                  cursor: 'pointer',
                                  '&:hover': { backgroundColor: '#f5f5f5' }
                                }}
                                onClick={() => window.open(`/sca-details/${id}?policy=${report.policy_id}&filter=all`, '_blank')}
                              >
                                <TableCell sx={{ py: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ color: '#4285f4', fontSize: '11px', fontWeight: '500' }}>
                                      {report.policy_id || `Policy ${index + 1}`}
                                    </Typography>
                                    <Chip 
                                      label={report.benchmark || 'CIS'}
                                      size="small" 
                                      sx={{ 
                                        height: 18, 
                                        fontSize: '9px', 
                                        backgroundColor: '#e8f5e8',
                                        color: '#2e7d32'
                                      }} 
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '11px' }}>
                                    {formatDate(report.end_scan)}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Box 
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: '#e8f5e8' },
                                      borderRadius: 1,
                                      px: 0.5
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Open in new tab with passed filter
                                      window.open(`/sca-details/${id}?policy=${report.policy_id}&filter=passed`, '_blank');
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontSize: '11px', color: '#34a853', fontWeight: '500' }}>
                                      {passed}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Box 
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: '#fce8e6' },
                                      borderRadius: 1,
                                      px: 0.5
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Open in new tab with failed filter
                                      window.open(`/sca-details/${id}?policy=${report.policy_id}&filter=failed`, '_blank');
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontSize: '11px', color: '#ea4335', fontWeight: '500' }}>
                                      {failed}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '11px' }}>{notApplicable}</Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 'bold' }}>{score}%</Typography>
                                    <Box sx={{ 
                                      width: 30, 
                                      height: 4, 
                                      backgroundColor: '#e0e0e0', 
                                      borderRadius: 2,
                                      overflow: 'hidden'
                                    }}>
                                      <Box sx={{ 
                                        width: `${score}%`, 
                                        height: '100%', 
                                        backgroundColor: score >= 80 ? '#34a853' : score >= 60 ? '#fbbc04' : '#ea4335',
                                        transition: 'width 0.3s ease'
                                      }} />
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>No SCA reports found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, gap: 1 }}>
                    <Button size="small" sx={{ minWidth: 'auto', p: 0.5, fontSize: '12px' }}>‹</Button>
                    <Typography variant="body2" sx={{ fontSize: '12px' }}>1</Typography>
                    <Button size="small" sx={{ minWidth: 'auto', p: 0.5, fontSize: '12px' }}>›</Button>
                  </Box>

                  {/* Detailed SCA Checks View */}
                  {selectedScaPolicy && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontSize: '14px', fontWeight: '600' }}>
                          {selectedScaPolicy.policy_id} - Detailed Checks
                        </Typography>
                        <IconButton size="small" onClick={() => setSelectedScaPolicy(null)}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      
                      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
                        <Chip 
                          label={`Total: ${getFilteredScaChecks(selectedScaPolicy).length}`}
                          size="small"
                          sx={{ backgroundColor: '#e3f2fd' }}
                        />
                        <Chip 
                          label={`Passed: ${selectedScaPolicy.passed}`}
                          size="small"
                          color="success"
                        />
                        <Chip 
                          label={`Failed: ${selectedScaPolicy.failed}`}
                          size="small"
                          color="error"
                        />
                        <Chip 
                          label={`Score: ${selectedScaPolicy.score}%`}
                          size="small"
                          sx={{ backgroundColor: '#fff3e0' }}
                        />
                      </Box>

                      <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontSize: '11px', fontWeight: '600' }}>Check ID</TableCell>
                              <TableCell sx={{ fontSize: '11px', fontWeight: '600' }}>Title</TableCell>
                              <TableCell sx={{ fontSize: '11px', fontWeight: '600' }}>Result</TableCell>
                              <TableCell sx={{ fontSize: '11px', fontWeight: '600' }}>Compliance</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getFilteredScaChecks(selectedScaPolicy).slice(0, 20).map((check, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell sx={{ py: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '10px', fontFamily: 'monospace' }}>
                                    {check.id}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1, maxWidth: 300 }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontSize: '10px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title={check.title}
                                  >
                                    {check.title}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Chip
                                    label={check.result}
                                    size="small"
                                    color={check.result === 'passed' ? 'success' : 'error'}
                                    sx={{ fontSize: '9px', height: 18 }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {check.compliance?.slice(0, 3).map((comp, compIdx) => (
                                      <Chip
                                        key={compIdx}
                                        label={`${comp.key}: ${comp.value}`}
                                        size="small"
                                        sx={{ 
                                          fontSize: '8px', 
                                          height: 16,
                                          backgroundColor: '#f0f0f0',
                                          color: '#666'
                                        }}
                                      />
                                    ))}
                                    {check.compliance?.length > 3 && (
                                      <Typography variant="caption" sx={{ fontSize: '8px', color: '#666' }}>
                                        +{check.compliance.length - 3} more
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {getFilteredScaChecks(selectedScaPolicy).length > 20 && (
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: '#666' }}>
                          Showing first 20 of {getFilteredScaChecks(selectedScaPolicy).length} checks
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Row 4: Packages - Full width */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ 
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
            }
          }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                pointerEvents: 'none'
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
                <Zoom in={true} timeout={500}>
                  <Box sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)', 
                    borderRadius: '50%', 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}>
                    <PackageIcon sx={{ fontSize: 28 }} />
                  </Box>
                </Zoom>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '0.5px' }}>
                    Package Inventory
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '13px' }}>
                    Complete system package analysis & insights
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
                <Fade in={!loadingPackages} timeout={800}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {(() => {
                      const stats = getPackageStats();
                      return (
                        <>
                          <Chip 
                            label={`${stats.total} Total`}
                            sx={{ 
                              backgroundColor: 'rgba(255,255,255,0.25)',
                              color: 'white',
                              fontWeight: 600,
                              border: '1px solid rgba(255,255,255,0.4)',
                              backdropFilter: 'blur(10px)'
                            }}
                          />
                          <Chip 
                            label={`${stats.deb} DEB`}
                            sx={{ 
                              backgroundColor: 'rgba(33,150,243,0.8)',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '11px'
                            }}
                          />
                        </>
                      );
                    })()}
                  </Box>
                </Fade>
                {loadingPackages && <CircularProgress size={20} sx={{ color: 'white' }} />}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/agents/${id}/packages`)}
                  startIcon={<VisibilityIcon />}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.6)',
                    fontWeight: 600,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  View All Packages
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Row 5: Security Alerts - Full width */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ border: '1px solid #e0e0e0' }}>
            <Box sx={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  borderRadius: '50%', 
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <WarningIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Security Alerts
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '12px' }}>
                    Real-time security incidents and threats
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={`${securityAlerts.length} Alerts`}
                  sx={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}
                />
                {loadingAlerts && <CircularProgress size={20} sx={{ color: 'white' }} />}
              </Box>
            </Box>
            <CardContent sx={{ p: 0 }}>
              {securityAlerts.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Timestamp</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Severity</TableCell>
                        <TableCell>Rule ID</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {securityAlerts.slice(0, 20).map((alert, index) => (
                        <TableRow key={alert.id || index} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {formatTimestamp(alert.timestamp || alert['@timestamp'])}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {alert.rule?.description || alert.description || 'No description'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getSeverityFromLevel(alert.rule?.level || 0)}
                              color={getSeverityColor(getSeverityFromLevel(alert.rule?.level || 0))}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {alert.rule?.id || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon fontSize="small" />
                              <Typography variant="body2">
                                {alert.location || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton size="small">
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <InfoIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#666', mb: 0.5 }}>
                    No Security Alerts
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '12px' }}>
                    This agent has no security alerts at the moment
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>



      </Grid>
      {/* Floating AI Assistant FAB */}
      <Tooltip
        title={`Ask AI about agent ${agent?.name || id}`}
      >
        <span>
          <Fab
            color="primary"
            onClick={handleOpenAiDialog}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1200,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 12px 30px rgba(102,126,234,0.5)'
            }}
          >
            <MagicIcon />
          </Fab>
        </span>
      </Tooltip>

      {/* AI Assistant Dialog */}
      <Dialog
        open={aiDialogOpen}
        onClose={handleCloseAiDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#020617',
            color: '#e5e7eb',
            borderRadius: 3,
            border: '1px solid rgba(148,163,184,0.35)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid rgba(148,163,184,0.35)', bgcolor: 'transparent' }}>
          AI Assistant for Agent {agent?.name || id}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: 360 }}>
            <Paper
              sx={{
                flex: 1,
                p: 2.5,
                backgroundColor: '#020617',
                borderRadius: 2,
                overflowY: 'auto',
                border: '1px solid rgba(30,64,175,0.6)'
              }}
            >
              {aiMessages.length === 0 && !aiLoading && !aiError && (
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 1.5,
                    borderRadius: 3,
                    backgroundColor: 'rgba(30,64,175,0.35)',
                    boxShadow: '0 18px 45px rgba(15,23,42,0.8)',
                    maxWidth: '80%'
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#e5e7eb' }}>
                    Welcome! Ask anything about this agent, for example: "Summarize this agent's status" or "What OS and version is this agent running?".
                  </Typography>
                </Box>
              )}
              {aiMessages.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  {msg.role === 'assistant' && (
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        mr: 1.2,
                        bgcolor: 'rgba(37,99,235,0.9)',
                        fontSize: 14
                      }}
                    >
                      AI
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: '75%',
                      px: 1.8,
                      py: 1.1,
                      borderRadius: 2.5,
                      backgroundColor: msg.role === 'user' ? 'rgba(59,130,246,0.95)' : 'rgba(15,23,42,0.95)',
                      color: '#e5e7eb',
                      boxShadow: msg.role === 'user'
                        ? '0 18px 40px rgba(37,99,235,0.55)'
                        : '0 14px 32px rgba(15,23,42,0.9)',
                      border: msg.role === 'user'
                        ? '1px solid rgba(129,140,248,0.9)'
                        : '1px solid rgba(31,41,55,0.9)'
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.75, display: 'block', mb: 0.3 }}
                    >
                      {msg.role === 'user' ? 'You' : 'Assistant'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize: 13.5 }}
                    >
                      {renderMessageContent(msg.content)}
                    </Typography>
                  </Box>
                  {msg.role === 'user' && (
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        ml: 1.2,
                        bgcolor: 'rgba(148,163,184,0.9)',
                        fontSize: 14
                      }}
                    >
                      You
                    </Avatar>
                  )}
                </Box>
              ))}
              {aiLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Thinking...</Typography>
                </Box>
              )}
              {aiError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {aiError}
                </Alert>
              )}
            </Paper>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: -0.5 }}>
              {aiSuggestedQuestions.map((q) => (
                <Button
                  key={q}
                  size="small"
                  variant="outlined"
                  disabled={aiLoading}
                  onClick={() => {
                    if (aiInputRef.current) {
                      aiInputRef.current.value = q;
                    }
                    handleSendAiPrompt();
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 999,
                    borderColor: 'rgba(148,163,184,0.6)',
                    color: '#e5e7eb',
                    fontSize: 12,
                    px: 1.5,
                    py: 0.25
                  }}
                >
                  {q}
                </Button>
              ))}
            </Box>

            <TextField
              placeholder="Ask a question about this agent..."
              fullWidth
              inputRef={aiInputRef}
              size="small"
              InputProps={{
                sx: {
                  borderRadius: 999,
                  backgroundColor: 'rgba(15,23,42,0.95)',
                  px: 1.5,
                  color: '#e5e7eb',
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(148,163,184,0.9)'
                  }
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      onClick={handleSendAiPrompt}
                      disabled={aiLoading}
                      sx={{
                        minWidth: 0,
                        p: 0.5,
                        borderRadius: '50%',
                        color: '#fde68a'
                      }}
                    >
                      <SendIcon />
                    </Button>
                  </InputAdornment>
                )
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!aiLoading) {
                    handleSendAiPrompt();
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAiDialog}>Close</Button>
          <Button onClick={handleSendAiPrompt} variant="contained" disabled={aiLoading}>
            {aiLoading ? 'Asking...' : 'Ask AI'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AgentDetails;
